import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Download,
  Star,
  BookOpen,
  FileText,
  Bookmark,
  Share2,
  ArrowRight,
} from 'lucide-react';
import { Resource, ResourceType, Class } from '@/types/library';

interface LibraryDashboardProps {
  onSelectResource: (resource: Resource) => void;
}

export default function LibraryDashboard({ onSelectResource }: LibraryDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState<Class | 'all'>('all');
  const [selectedType, setSelectedType] = useState<ResourceType | 'all'>('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [bookmarks, setBookmarks] = useState<string[]>([]);

  // Mock resources data
  const allResources: Resource[] = [
    {
      id: '1',
      title: 'New General Mathematics for JSS1',
      subject: 'Mathematics',
      class: 'JSS1',
      type: 'textbook',
      author: 'M.F. Macrae',
      description: 'Comprehensive mathematics textbook covering algebra, geometry, and arithmetic',
      fileSize: 45.2,
      pages: 320,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-15'),
      downloads: 1250,
      rating: 4.8,
      reviews: 156,
      tags: ['algebra', 'geometry', 'arithmetic'],
    },
    {
      id: '2',
      title: 'English Language for JSS1 - Grammar Guide',
      subject: 'English',
      class: 'JSS1',
      type: 'guide',
      author: 'Ayo Banjo',
      description: 'Complete guide to English grammar rules and usage',
      fileSize: 32.5,
      pages: 245,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-10'),
      downloads: 980,
      rating: 4.7,
      reviews: 128,
      tags: ['grammar', 'composition', 'vocabulary'],
    },
    {
      id: '3',
      title: 'Integrated Science Notes - JSS1',
      subject: 'Science',
      class: 'JSS1',
      type: 'notes',
      author: 'Dr. Oludare',
      description: 'Comprehensive study notes for integrated science',
      fileSize: 28.8,
      pages: 180,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-08'),
      downloads: 1520,
      rating: 4.9,
      reviews: 189,
      tags: ['biology', 'chemistry', 'physics'],
    },
    {
      id: '4',
      title: 'History of West Africa - JSS2',
      subject: 'History',
      class: 'JSS2',
      type: 'textbook',
      author: 'Toyin Falola',
      description: 'Detailed study of West African history and civilizations',
      fileSize: 52.1,
      pages: 380,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-05'),
      downloads: 845,
      rating: 4.6,
      reviews: 92,
      tags: ['history', 'civilizations', 'culture'],
    },
    {
      id: '5',
      title: 'WAEC Past Papers - Mathematics 2023',
      subject: 'Mathematics',
      class: 'SS2',
      type: 'past-paper',
      description: 'Official WAEC past examination papers for Mathematics',
      fileSize: 8.5,
      pages: 45,
      fileUrl: '#',
      uploadedAt: new Date('2025-10-20'),
      downloads: 3200,
      rating: 4.9,
      reviews: 342,
      tags: ['exam', 'past-paper', 'waec'],
    },
    {
      id: '6',
      title: 'Geography Worksheet - Physical Features',
      subject: 'Geography',
      class: 'JSS2',
      type: 'worksheet',
      description: 'Practice worksheet covering physical geography features',
      fileSize: 5.2,
      pages: 25,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-12'),
      downloads: 620,
      rating: 4.5,
      reviews: 67,
      tags: ['geography', 'practice', 'worksheet'],
    },
    {
      id: '7',
      title: 'Civic Education Study Guide - SS1',
      subject: 'Civic Education',
      class: 'SS1',
      type: 'guide',
      description: 'Comprehensive study guide for civic education',
      fileSize: 35.8,
      pages: 210,
      fileUrl: '#',
      uploadedAt: new Date('2025-11-01'),
      downloads: 510,
      rating: 4.4,
      reviews: 58,
      tags: ['civics', 'governance', 'rights'],
    },
    {
      id: '8',
      title: 'Mathematics Reference - SS3',
      subject: 'Mathematics',
      class: 'SS3',
      type: 'reference',
      description: 'Advanced mathematics reference book for SS3',
      fileSize: 41.3,
      pages: 295,
      fileUrl: '#',
      uploadedAt: new Date('2025-10-28'),
      downloads: 1100,
      rating: 4.7,
      reviews: 145,
      tags: ['advanced', 'reference', 'calculus'],
    },
  ];

  const subjects = [
    { name: 'Mathematics', icon: '📐' },
    { name: 'English', icon: '📚' },
    { name: 'Science', icon: '🔬' },
    { name: 'History', icon: '📖' },
    { name: 'Geography', icon: '🗺️' },
    { name: 'Civic Education', icon: '🏛️' },
  ];

  const classes: Class[] = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];
  const types: ResourceType[] = ['textbook', 'guide', 'notes', 'worksheet', 'past-paper', 'reference'];

  // Filter resources
  const filteredResources = allResources.filter((resource) => {
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesClass = selectedClass === 'all' || resource.class === selectedClass;
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    const matchesSubject = selectedSubject === 'all' || resource.subject === selectedSubject;

    return matchesSearch && matchesClass && matchesType && matchesSubject;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const getTypeColor = (type: ResourceType) => {
    const colors: Record<ResourceType, string> = {
      textbook: 'bg-blue-100 text-blue-800',
      guide: 'bg-green-100 text-green-800',
      notes: 'bg-purple-100 text-purple-800',
      worksheet: 'bg-orange-100 text-orange-800',
      'past-paper': 'bg-red-100 text-red-800',
      reference: 'bg-cyan-100 text-cyan-800',
    };
    return colors[type];
  };

  const getTypeIcon = (type: ResourceType) => {
    const icons: Record<ResourceType, string> = {
      textbook: '📕',
      guide: '📗',
      notes: '📝',
      worksheet: '📄',
      'past-paper': '📋',
      reference: '📕',
    };
    return icons[type];
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">Digital Library</h1>
          <p className="text-muted-foreground">Access textbooks, notes, and study materials</p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search by title, subject, or tag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-border"
          />
        </div>
      </div>

      {/* Featured Subjects */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-primary">Browse by Subject</h2>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        >
          {subjects.map((subject) => (
            <motion.button
              key={subject.name}
              variants={itemVariants}
              onClick={() => setSelectedSubject(subject.name === selectedSubject ? 'all' : subject.name)}
              className={`p-4 rounded-lg border-2 transition-all duration-300 text-center ${
                selectedSubject === subject.name
                  ? 'bg-primary/10 border-primary'
                  : 'border-border hover:border-secondary hover:bg-muted/50'
              }`}
            >
              <div className="text-3xl mb-2">{subject.icon}</div>
              <p className="text-sm font-medium text-foreground">{subject.name}</p>
            </motion.button>
          ))}
        </motion.div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Class</label>
          <Select value={selectedClass} onValueChange={(value) => setSelectedClass(value as Class | 'all')}>
            <SelectTrigger className="border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classes.map((cls) => (
                <SelectItem key={cls} value={cls}>
                  {cls}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Type</label>
          <Select value={selectedType} onValueChange={(value) => setSelectedType(value as ResourceType | 'all')}>
            <SelectTrigger className="border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Subject</label>
          <Select value={selectedSubject} onValueChange={setSelectedSubject}>
            <SelectTrigger className="border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subjects</SelectItem>
              {subjects.map((subject) => (
                <SelectItem key={subject.name} value={subject.name}>
                  {subject.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filteredResources.length}</span> resources
        </p>
        <Button variant="outline" size="sm" className="border-border">
          Sort by Latest
        </Button>
      </div>

      {/* Resources Grid */}
      {filteredResources.length > 0 ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredResources.map((resource) => (
            <motion.div key={resource.id} variants={itemVariants}>
              <Card className="overflow-hidden border-border hover:shadow-lg hover:border-secondary transition-all duration-300 h-full flex flex-col">
                {/* Card Header */}
                <div className="p-6 flex-1 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-3xl">{getTypeIcon(resource.type)}</div>
                    <Badge className={getTypeColor(resource.type)}>
                      {resource.type.replace('-', ' ')}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-primary line-clamp-2">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{resource.subject}</p>
                  </div>

                  {resource.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {resource.description}
                    </p>
                  )}

                  {/* Metadata */}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {resource.pages ? `${resource.pages} pages` : ''}
                      </span>
                      <span className="text-muted-foreground">{resource.fileSize} MB</span>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{resource.downloads} downloads</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-accent text-accent" />
                        <span className="font-semibold text-foreground">{resource.rating}</span>
                        <span className="text-muted-foreground">({resource.reviews})</span>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {resource.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {resource.tags.slice(0, 2).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="p-6 border-t border-border space-y-3">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setBookmarks(
                        bookmarks.includes(resource.id)
                          ? bookmarks.filter(id => id !== resource.id)
                          : [...bookmarks, resource.id]
                      )}
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-2 border-border"
                    >
                      <Bookmark
                        className={`w-4 h-4 ${
                          bookmarks.includes(resource.id) ? 'fill-current' : ''
                        }`}
                      />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-2 border-border"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button className="w-full bg-primary hover:bg-primary/90 gap-2">
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <Card className="p-12 text-center border-border">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-lg font-semibold text-foreground mb-2">No resources found</p>
          <p className="text-muted-foreground">
            Try adjusting your filters or search query
          </p>
        </Card>
      )}
    </div>
  );
}
